import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';

// ============================================================================
// Types
// ============================================================================

interface CallCardDispositionProps {
  callCard: any;
  isReadOnly?: boolean;
  callCardNotClosed?: boolean;
  isEditingClosedCallCard?: boolean;
  dispositionOptions?: any[];
  dispositionGroupBy?: string | null;
  onDispositionChange?: (data: { value: any }) => void;
  onFieldChange?: (data: { field: string; value: any }) => void;
  onDisabledFieldClick?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function CallCardDisposition({
  callCard,
  isReadOnly = false,
  callCardNotClosed = true,
  isEditingClosedCallCard = false,
  dispositionOptions = [],
  dispositionGroupBy = null,
  onDispositionChange,
  onFieldChange,
  onDisabledFieldClick,
}: CallCardDispositionProps) {
  const { theme } = useTheme();

  const [localDisposition, setLocalDisposition] = useState<number | null>(null);
  const [localNotes, setLocalNotes] = useState('');

  // Sync localDisposition from callCard.callDisposition
  useEffect(() => {
    setLocalDisposition(callCard?.callDisposition ?? null);
  }, [callCard?.callDisposition]);

  // Sync localNotes from callCard.dispositionNotes
  useEffect(() => {
    setLocalNotes(callCard?.dispositionNotes || '');
  }, [callCard?.dispositionNotes]);

  const canEdit = useMemo((): boolean => {
    if (isReadOnly) return false;
    return callCardNotClosed || isEditingClosedCallCard;
  }, [isReadOnly, callCardNotClosed, isEditingClosedCallCard]);

  const handleDispositionChange = useCallback((value: string | number | null) => {
    setLocalDisposition(value as number | null);
    onDispositionChange?.({ value });
    onFieldChange?.({ field: 'callDisposition', value });
  }, [onDispositionChange, onFieldChange]);

  const handleNotesChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalNotes(event.target.value);
    onFieldChange?.({ field: 'dispositionNotes', value: event.target.value });
  }, [onFieldChange]);

  const handleDisabledClick = useCallback(() => {
    onDisabledFieldClick?.();
  }, [onDisabledFieldClick]);

  return (
    <div className={`details-panel disposition-section ${theme}`}>
      <div className="panel-header">Call Disposition</div>
      <div className="panel-body">
        {/* Transparent overlay to capture clicks when disabled */}
        {isReadOnly && (
          <div className="disabled-overlay" onClick={handleDisabledClick}></div>
        )}

        {/* Disposition */}
        <div className="form-row">
          <div className="form-group form-group-full">
            <label className="field-label">Disposition</label>
            <Dropdownlist
              value={localDisposition}
              items={dispositionOptions}
              labelKey="dispositionValue"
              valueKey="dispositionCode"
              groupBy={dispositionGroupBy}
              placeholder="Select Disposition"
              disabled={!canEdit}
              searchable={true}
              sortItems={false}
              onChange={handleDispositionChange}
            />
          </div>
        </div>

        {/* Disposition Notes */}
        <div className="form-row">
          <div className="form-group form-group-full">
            <label className="field-label">Disposition Notes</label>
            <textarea
              className="form-textarea"
              rows={4}
              placeholder="Enter disposition notes here.."
              value={localNotes}
              disabled={!canEdit}
              onChange={handleNotesChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).CallCardDispositionReact = CallCardDisposition;
